import { Box, CircularProgress, Paper, Tab, Tabs } from "@mui/material"
import type { AnyRoute } from "@tanstack/react-router"
import {
  Link,
  Outlet,
  createFileRoute,
  retainSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router"
import type { ReactElement } from "react"
import React, { useEffect, useMemo } from "react"
import { z } from "zod"

import type { ProductVersionStatus } from "@/graphql/codegen/graphql"
import { ComputedProductStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useProductActionsStore } from "@/screens/Companies/products/stores/useProductsActions"
import { ProductActions } from "@/screens/Companies/products/types/schema"
import { usePurchaseOrderDialog } from "@/screens/Procurement/hooks/usePurchaseOrderDialog"
import { PurchaseOrderDialog } from "@/screens/Procurement/PurchaseOrder/components/dialogs/PurchaseOrderDialog"
import { hasValidShipFromLocations } from "@/screens/Procurement/PurchaseOrder/utils/common"
import ProductEditingDialogs from "@/screens/Products/components/ProductEditingDialogs"
import ProductHeader from "@/screens/Products/components/ProductHeader"
import ProductHeaderActions from "@/screens/Products/components/ProductHeaderActions"
import { uomLoader } from "@/screens/WarehouseReceiving/loaders"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import {
  ProductStoreProvider,
  selectProduct,
  useCategory,
  useClassification,
  useProductActions,
  useProductManager,
  useProductStore,
} from "@/stores/useProductStore"
import theme from "@/styles/mui-theme"

const createTabList = (productId: string) => [
  { label: "Information", path: `/app/product/${productId}/edit/information` },
  { label: "Regulatory", path: `/app/product/${productId}/edit/regulatory` },
  { label: "Instructions", path: `/app/product/${productId}/edit/instructions` },
  { label: "Pricing", path: `/app/product/${productId}/edit/pricing` },
  { label: "Quality", path: `/app/product/${productId}/edit/quality` },
  { label: "Storage", path: `/app/product/${productId}/edit/storage` },
  { label: "Cost", path: `/app/product/${productId}/edit/costs` },
]

const ProductEditContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { productId: queryProductId } = useParams({
    from: "/app/product/$productId/edit",
  })
  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout" })

  const tabs = useMemo(() => createTabList(queryProductId), [queryProductId])

  const currentTab = tabs.find((tab) => location.pathname.startsWith(tab.path)) || tabs[0]

  const { getProduct } = useProductActions()
  const product = useProductStore(selectProduct)
  const productManagerName = useProductManager()
  const classificationName = useClassification()
  const categoryName = useCategory()
  const fetchingProduct = useProductStore((state) => state?.fetching)
  const fetchingProductError = useProductStore((state) => state?.error)
  const onAction = useProductActionsStore((state) => state.onAction)
  const setProductAtAction = useProductActionsStore((state) => state.setProductAtAction)
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  useEffect(() => {
    if (product) {
      setProductAtAction(product)
    }
  }, [product, setProductAtAction])

  useEffect(() => {
    if (queryProductId) {
      getProduct(Number(queryProductId), version)
    }
  }, [queryProductId, version, getProduct])

  useEffect(() => {
    if (version && fetchingProductError) {
      notify({
        type: NotificationType.Error,
        message: "Unable to load version. Please try again.",
      })

      navigate({
        search: (prev: Record<string, any>) => ({
          ...prev,
          version: undefined,
        }),
        replace: true,
      })
    }
  }, [version, fetchingProductError, navigate, notify])

  const {
    open,
    fetching,
    handleOpen: handleStartPurchaseOrderInternal,
    handleClose,
    handleSubmit: handlePurchaseOrderSubmit,
  } = usePurchaseOrderDialog(product ? [product] : null)

  const refreshProduct = async () => {
    await getProduct(Number(queryProductId), version)
  }

  const handleStartPurchaseOrder = async () => {
    await refreshProduct()
    handleStartPurchaseOrderInternal()
  }

  const handleToggleProductStatus = async (isActive: boolean) => {
    const action = isActive ? ProductActions.DEACTIVATE_PRODUCT : ProductActions.ACTIVATE_PRODUCT

    await onAction(action)
    await getProduct(Number(queryProductId), version)
  }

  const hasValidShipFrom = hasValidShipFromLocations(product)

  if (!product || fetchingProduct) {
    return (
      <Box className='flex w-full items-center justify-center'>
        <CircularProgress
          size={48}
          className='transition-opacity duration-300 ease-in-out'
          sx={{
            color: theme.palette.primary.main,
          }}
        />
      </Box>
    )
  }

  return (
    <>
      {open && product.company && (
        <PurchaseOrderDialog
          fetching={fetching}
          onClose={handleClose}
          onSubmit={handlePurchaseOrderSubmit}
          company={product.company}
          products={[product]}
        />
      )}
      <ProductEditingDialogs product={product} onSubmit={refreshProduct} />
      <Box className='size-full'>
        <div className='flex h-full flex-col gap-4'>
          <Paper elevation={0} className='flex min-h-0 grow flex-col space-y-7 bg-white'>
            <ProductHeaderActions
              product={product}
              onStartPurchaseOrder={handleStartPurchaseOrder}
              hasValidShipFrom={hasValidShipFrom}
            />
            <Box className='space-y-2.5'>
              <ProductHeader
                publicId={product.publicId}
                externalSystemId={product.externalSystemId}
                isActive={product.computedProductStatus === ComputedProductStatus.Active}
                productDescription={product.companyProductDescription ?? ""}
                vendorDescription={product.vendorDescription ?? ""}
                vendorSku={product.vendorSku ?? ""}
                productManager={productManagerName}
                classification={classificationName}
                category={categoryName}
                companyName={product.company?.name ?? ""}
                onToggleProductStatus={handleToggleProductStatus}
                productVersionId={product.versionId as number}
                productVersionReleaseDate={product.versionReleaseDate}
                productVersionStatus={product.versionStatus as ProductVersionStatus}
                productVersions={product.versions ?? []}
              />
            </Box>
            <Box className='flex min-h-0 w-full grow flex-col'>
              <Tabs value={currentTab.path} variant='scrollable' scrollButtons='auto'>
                {tabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} value={tab.path} component={Link} to={tab.path} />
                ))}
              </Tabs>
              <Box className='min-h-0 grow overflow-auto py-6'>
                <Outlet />
              </Box>
            </Box>
          </Paper>
        </div>
      </Box>
    </>
  )
}

const ProductEditLayout = (): ReactElement => {
  return (
    <ProductStoreProvider>
      <ProductEditContent />
    </ProductStoreProvider>
  )
}

// Export the component for testing
export const ProductComponent = ProductEditLayout

const SearchParamsValidator = z.object({
  version: z.number().optional(),
})

export const Route: AnyRoute = createFileRoute("/app/product/$productId/edit/_layout")({
  validateSearch: SearchParamsValidator,
  search: {
    middlewares: [retainSearchParams(["version"])],
  },
  loader: uomLoader,
  component: ProductComponent,
})
